import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  PhoneAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  ConfirmationResult,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth } from '../firebase';
import { api } from '../hooks/useApi';
import { identifyUser } from '../hooks/useAnalytics';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isGuest: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithPhone: (phoneNumber: string) => Promise<ConfirmationResult>;
  confirmPhoneCode: (verificationId: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getIdToken: () => Promise<string>;
  refreshUser: () => Promise<void>;
  continueAsGuest: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(null!);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(() => !!localStorage.getItem('airscribe_guest_token'));

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
      if (firebaseUser) {
        localStorage.setItem('airscribe_loggedIn', 'true');
        identifyUser(firebaseUser.uid, { email: firebaseUser.email ?? undefined });
        // Auto-link guest account if one exists
        const guestToken = localStorage.getItem('airscribe_guest_token');
        if (guestToken) {
          try { await api.linkGuestAccount(guestToken); } catch { /* best effort */ }
          localStorage.removeItem('airscribe_guest_token');
          setIsGuest(false);
        }
      } else {
        localStorage.removeItem('airscribe_loggedIn');
      }
    });
    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const signInWithPhone = async (phoneNumber: string): Promise<ConfirmationResult> => {
    if (Capacitor.isNativePlatform()) {
      return new Promise<ConfirmationResult>((resolve, reject) => {
        const cleanup = async () => {
          await codeSentHandle.then(h => h.remove());
          await failedHandle.then(h => h.remove());
        };
        const codeSentHandle = FirebaseAuthentication.addListener('phoneCodeSent', async (event) => {
          await cleanup();
          resolve({
            verificationId: event.verificationId,
            confirm: async (code: string) => {
              const credential = PhoneAuthProvider.credential(event.verificationId, code);
              return signInWithCredential(auth, credential);
            },
          } as unknown as ConfirmationResult);
        });
        const failedHandle = FirebaseAuthentication.addListener('phoneVerificationFailed', async (event) => {
          await cleanup();
          reject(new Error(event.message));
        });
        FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber }).catch(async (err) => {
          await cleanup();
          reject(err);
        });
      });
    } else {
      if (!(window as any).recaptchaVerifier) {
        (window as any).recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      }
      return signInWithPhoneNumber(auth, phoneNumber, (window as any).recaptchaVerifier);
    }
  };

  const confirmPhoneCode = async (verificationId: string, code: string) => {
    const credential = PhoneAuthProvider.credential(verificationId, code);
    await signInWithCredential(auth, credential);
  };

  const signInWithGoogle = async () => {
    if (Capacitor.isNativePlatform()) {
      console.log('[Auth] Starting native Google sign-in...');
      try {
        const result = await FirebaseAuthentication.signInWithGoogle();
        console.log('[Auth] Native sign-in result:', JSON.stringify(result));
        const credential = GoogleAuthProvider.credential(result.credential?.idToken);
        await signInWithCredential(auth, credential);
      } catch (err) {
        console.error('[Auth] Native Google sign-in error:', err);
        throw err;
      }
    } else {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    }
  };

  const signOut = async () => {
    if (!auth.currentUser) {
      // Guest sign out
      localStorage.removeItem('airscribe_guest_token');
      setIsGuest(false);
      return;
    }
    await firebaseSignOut(auth);
  };

  const continueAsGuest = async () => {
    const { guestToken } = await api.createGuestAccount();
    localStorage.setItem('airscribe_guest_token', guestToken);
    setIsGuest(true);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const getIdToken = async () => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    return auth.currentUser.getIdToken();
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      setUser({ ...auth.currentUser });
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, isGuest, signIn, signUp, signInWithGoogle, signInWithPhone, confirmPhoneCode, signOut, resetPassword, getIdToken, refreshUser, continueAsGuest }}>
      {children}
    </AuthContext.Provider>
  );
}
